import { CheckCircle as CheckCircleIcon, Clear as ClearIcon, Link as LinkIcon } from '@mui/icons-material'
import { Box, Button, LinearProgress, TextField, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridOverlay, GridRenderCellParams } from '@mui/x-data-grid'
import { useSearchUsers } from '@src/data/api/user-api/user-api'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantDashboardUser } from '@src/data/types/MerchantDashboardUserSchema'
import { debounce } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SearchUsersList = () => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [usersList, isFetchingUsers] = useSearchUsers(searchTerm)
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'fullName',
        headerName: t('common.name'),
        flex: 2,
      },
      {
        field: 'email',
        headerName: t('common.email'),
        flex: 2,
      },
      {
        field: 'accountEnabled',
        headerName: t('common.state'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        cellClassName: 'vertical-center-cell',
        renderCell: (params: GridRenderCellParams<MerchantDashboardUser, boolean>) => {
          return (
            <>
              {params?.value ? (
                <Tooltip title={t('common.enable')}>
                  <CheckCircleIcon sx={{ color: 'green' }} />
                </Tooltip>
              ) : (
                <Tooltip title={t('common.disable')}>
                  <ClearIcon sx={{ color: 'red' }} />
                </Tooltip>
              )}
            </>
          )
        },
      },
      {
        field: 'lastConnection',
        headerName: t('common.lastConnection'),
        flex: 2,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'merchantId',
        headerName: t('common.merchant'),
        flex: 2,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<Merchant, string>) => {
          const prefix = params.value?.includes('alis') || params.value?.includes('mono') ? 'merchant' : 'banner'
          return (
            <Button href={`/${prefix}/${params.value}/view`}>
              <Tooltip title={t('common.viewMerchant')}>
                <LinkIcon color="primary" />
              </Tooltip>
            </Button>
          )
        },
      },
    ],
    [t],
  )

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const debounceOnChange = debounce(handleSearch, 500)

  const CustomNoRowsOverlay = () => (
    <GridOverlay>
      {searchTerm.length < 3 && (
        <Typography variant="body1" style={{ marginTop: '20px', textAlign: 'center' }}>
          {t('common.typeToStartSearch')}
        </Typography>
      )}
      {searchTerm.length > 2 && (
        <Typography variant="body1" style={{ marginTop: '20px', textAlign: 'center' }}>
          {t('common.searchYieldsNoResults')}
        </Typography>
      )}
    </GridOverlay>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 1 }}>
      <TextField
        sx={{ width: 300 }}
        label={t('common.searchUser')}
        placeholder={t('common.name')}
        onChange={(e) => debounceOnChange(e.target.value)}
        defaultValue=""
        size="medium"
      />
      <Box sx={{ width: '100%', position: 'relative' }}>
        {isFetchingUsers ? (
          <LinearProgress sx={{ height: '4px', position: 'absolute', top: 0, left: 0, right: 0 }} />
        ) : (
          <DataGrid
            sx={{
              minHeight: 400,
              '& .MuiDataGrid-root': {
                border: 'none',
              },
            }}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 25,
                },
              },
            }}
            pageSizeOptions={[25, 50, 100]}
            disableColumnFilter
            paginationMode="server"
            disableRowSelectionOnClick
            rowCount={usersList.length}
            rows={usersList}
            columns={columns}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        )}
      </Box>
    </Box>
  )
}
export default SearchUsersList
